import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  GraduationCap,
  Award,
  Stethoscope,
  CalendarDays,
  Clock,
  ArrowLeft,
  CheckCircle2,
  Calendar,
} from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

export const DoctorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/doctors/${slug}`);
        if (!res.ok) throw new Error('Doctor not found');
        const data = await res.json();
        if (!cancelled) setDoctor(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load doctor');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#14D1A2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading doctor profile…</p>
        </div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Doctor not found</h1>
          <p className="text-gray-600 mb-6">{error || 'We could not locate this doctor profile.'}</p>
          <Button onClick={() => navigate('/doctors')} variant="outline" data-testid="back-to-doctors-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Doctors
          </Button>
        </div>
      </div>
    );
  }

  const bookHref = `/book-appointment?doctor=${encodeURIComponent(doctor.name)}&service=${encodeURIComponent(doctor.specialization || '')}`;

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="doctor-profile-page">
      {/* Hero */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4">
          <Link to="/doctors" className="inline-flex items-center text-white/90 hover:text-white mb-6 text-sm" data-testid="back-to-doctors-link">
            <ArrowLeft className="w-4 h-4 mr-1" /> All doctors
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-1">
              <div className="aspect-square max-w-xs mx-auto md:mx-0 rounded-2xl overflow-hidden shadow-2xl bg-white">
                <img
                  src={doctor.image}
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=14D1A2&color=fff&size=400`;
                  }}
                />
              </div>
            </div>
            <div className="md:col-span-2 text-white">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-3">
                {doctor.specialization}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{doctor.name}</h1>
              <p className="text-base md:text-lg text-gray-100 mb-6 leading-relaxed">
                {doctor.bio || `Experienced ${doctor.specialization} specialist with a patient-first approach.`}
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm md:text-base mb-6">
                <span className="inline-flex items-center gap-2"><Award className="w-4 h-4" /> {doctor.experience}</span>
                <span className="inline-flex items-center gap-2"><GraduationCap className="w-4 h-4" /> {doctor.education}</span>
              </div>
              <Link to={bookHref}>
                <Button size="lg" className="bg-white text-[#1C5C62] hover:bg-gray-100 font-semibold" data-testid="profile-book-cta">
                  <Calendar className="w-5 h-5 mr-2" />
                  Request Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* Qualifications */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-500">
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Qualifications</span>
                </div>
                <p className="text-gray-900 font-medium">{doctor.education}</p>
              </CardContent>
            </Card>
            {/* Experience */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-500">
                  <Award className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Experience</span>
                </div>
                <p className="text-gray-900 font-medium">{doctor.experience}</p>
              </CardContent>
            </Card>
            {/* Schedule */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-500">
                  <CalendarDays className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Consultation</span>
                </div>
                <p className="text-gray-900 font-medium">{doctor.schedule?.days || '—'}</p>
                <p className="text-sm text-gray-600 mt-1 inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {doctor.schedule?.hours || doctor.availability}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Conditions Treated */}
          {doctor.conditions && doctor.conditions.length > 0 && (
            <Card className="border-0 shadow-md mb-10">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-5">
                  <Stethoscope className="w-5 h-5 text-[#14D1A2]" />
                  <h2 className="text-2xl font-bold text-gray-900">Conditions Treated</h2>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="conditions-list">
                  {doctor.conditions.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-[#14D1A2] flex-shrink-0 mt-1" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* FAQs */}
          {doctor.faqs && doctor.faqs.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {doctor.faqs.map((f, i) => (
                  <AccordionItem
                    key={`${i}-${f.question}`}
                    value={`faq-${i}`}
                    className="bg-white border-0 shadow-sm rounded-lg px-6"
                  >
                    <AccordionTrigger className="text-base md:text-lg font-semibold text-gray-900 hover:text-[#14D1A2] py-4 text-left">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed pb-4">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Bottom CTA */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
            <CardContent className="p-8 md:p-10 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to consult {doctor.name.split(' ').slice(0, 2).join(' ')}?
              </h3>
              <p className="text-gray-100 mb-6">
                Submit a request and our team will confirm a suitable slot on WhatsApp or phone.
              </p>
              <Link to={bookHref}>
                <Button size="lg" className="bg-white text-[#1C5C62] hover:bg-gray-100 font-semibold" data-testid="profile-book-cta-bottom">
                  <Calendar className="w-5 h-5 mr-2" />
                  Request Appointment
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DoctorProfile;
